import { InjectionToken } from '@angular/core';
import type {
    HordeGameIntentDocument,
    HordeIntent,
    HostSecretVaultState,
    PersistedHordeGame,
    PlayerSecretViewState,
    PublicHistoryEntry,
    ValidatedHordeGameIntentDocument,
} from '@animus/type/domain';
import type { Observable } from 'rxjs';

export interface HordeAuthorityState {
    game: PersistedHordeGame;
    vault: HostSecretVaultState;
}

export interface HordeAuthorityTransitionResult extends HordeAuthorityState {
    acceptedEvent: PublicHistoryEntry;
    vaultChanged: boolean;
}

export type HordeAuthorityTransition = (
    state: HordeAuthorityState,
    intent: HordeIntent
) => HordeAuthorityTransitionResult;

export interface HordeCommitOutcome {
    duplicate: boolean;
    game: PersistedHordeGame;
    playerSecretViews: PlayerSecretViewState[];
    revision: number;
    status: 'fullyProjected';
}

export type HordeCommitFailureState = 'notCommitted' | 'committedProjectionPending' | 'rejected';

/** Typed failure at the authoritative persistence boundary. */
export class HordeCommitError extends Error {
    constructor(
        message: string,
        readonly state: HordeCommitFailureState,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'HordeCommitError';
    }
}

export interface RejectHordeIntentInput {
    intentId: string;
    reason: string;
    updatedAt: string;
}

export type HordeSessionLoadResult =
    | { status: 'ready'; value: PersistedHordeGame }
    | { status: 'notFound' }
    | { status: 'corrupt'; preservedValue: unknown }
    | { status: 'incompatible'; preservedValue: unknown; schemaVersion: number }
    | { status: 'unavailable'; message: string };

/** Public, non-secret summary used by the canonical home index. */
export interface HordeSessionSummary {
    gameId: string;
    lifecycle: PersistedHordeGame['lifecycle'];
    name: string;
    players: number;
    revision: number;
    updatedAt: string;
}

/**
 * Durable boundary for the Horde state machine.
 *
 * The host-only authority document atomically contains the public rules state and secret vault. Public sessions
 * and player-scoped secret views are derived projections; callers never receive the vault through an observable
 * intended for UI consumption.
 */
export interface HordeSessionPersistence {
    commitAtHost(intent: HordeIntent, transition: HordeAuthorityTransition): Promise<HordeCommitOutcome>;
    /**
     * Removes a session's public projection and best-effort clears its event/intent/secret-view docs. Optional so
     * existing in-memory test doubles outside this boundary are not forced to implement a deletion they never
     * exercise.
     */
    deleteSession?(gameId: string): Promise<void>;
    enqueueIntent(intent: HordeIntent, outboxScopeId: string): Promise<HordeGameIntentDocument>;
    initializeAuthority(state: HordeAuthorityState): Promise<void>;
    listPublicSessions(): Promise<HordeSessionSummary[]>;
    loadAuthority(gameId: string, hostDeviceId: string): Promise<HordeAuthorityState | null>;
    loadPlayerSecretView(gameId: string, playerId: string): Promise<PlayerSecretViewState | null>;
    loadPublicSession(gameId: string): Promise<PersistedHordeGame | null>;
    loadPublicSessionResult(gameId: string): Promise<HordeSessionLoadResult>;
    observeAuthoredIntents(gameId: string, actorDeviceId: string): Observable<HordeGameIntentDocument[]>;
    observePendingIntents(gameId: string): Observable<ValidatedHordeGameIntentDocument[]>;
    observePlayerSecretView(gameId: string, playerId: string): Observable<PlayerSecretViewState | null>;
    observePublicSession(gameId: string): Observable<PersistedHordeGame | null>;
    observePublicSessions(): Observable<HordeSessionSummary[]>;
    reconcileIncompleteCommits(gameId: string): Promise<void>;
    rejectIntent(input: RejectHordeIntentInput): Promise<void>;
}

export const HORDE_SESSION_PERSISTENCE = new InjectionToken<HordeSessionPersistence>('HORDE_SESSION_PERSISTENCE');
